/**
 * Y-08: career OVR/POT line chart — pure SVG, no visx.
 * Shown in the "生涯曲线" tab of the mobile player profile.
 */

import i18n from "../../util/i18n.ts";

type SeasonRating = {
	season: number;
	ovr: number;
	pot: number;
};

type Props = {
	ratings: SeasonRating[];
};

const W = 320;
const H = 160;
const PAD = { top: 10, right: 16, bottom: 28, left: 28 };

const innerW = W - PAD.left - PAD.right;
const innerH = H - PAD.top - PAD.bottom;

const Y_MIN = 20;
const Y_MAX = 100;

const scaleX = (i: number, n: number) =>
	n <= 1 ? PAD.left + innerW / 2 : PAD.left + (i / (n - 1)) * innerW;

const scaleY = (v: number) =>
	PAD.top + innerH - ((v - Y_MIN) / (Y_MAX - Y_MIN)) * innerH;

const toPath = (points: [number, number][]) =>
	points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");

export const CareerOverviewChart = ({ ratings }: Props) => {
	if (ratings.length === 0) {
		return null;
	}

	const n = ratings.length;

	const ovrPoints = ratings.map<[number, number]>((r, i) => [
		scaleX(i, n),
		scaleY(Math.max(Y_MIN, Math.min(Y_MAX, r.ovr))),
	]);
	const potPoints = ratings.map<[number, number]>((r, i) => [
		scaleX(i, n),
		scaleY(Math.max(Y_MIN, Math.min(Y_MAX, r.pot))),
	]);

	// Y gridlines at 40, 60, 80, 100
	const gridY = [40, 60, 80, 100];

	// X-axis: show season labels sparsely so they don't overlap
	const labelEvery = Math.ceil(n / 6);
	const seasonLabels = ratings
		.map((r, i) => ({ season: r.season, i }))
		.filter(({ i }) => i === 0 || i === n - 1 || i % labelEvery === 0);

	const ovrLabel = i18n.t("title.Ovr", {
		ns: "cols",
		defaultValue: "Overall",
	});
	const potLabel = i18n.t("title.Pot", {
		ns: "cols",
		defaultValue: "Potential",
	});

	return (
		<div className="d-flex flex-column align-items-center my-2">
			<svg
				viewBox={`0 0 ${W} ${H}`}
				width="100%"
				style={{ maxWidth: W }}
				role="img"
				aria-label={i18n.t("playerProfile.careerChartLabel", {
					ns: "ui",
					defaultValue: "Career overall and potential chart",
				})}
			>
				{/* Grid lines */}
				{gridY.map((v) => {
					const y = scaleY(v);
					return (
						<g key={v}>
							<line
								x1={PAD.left}
								x2={PAD.left + innerW}
								y1={y}
								y2={y}
								stroke="var(--bs-border-color)"
								strokeWidth={0.5}
								opacity={0.6}
							/>
							<text
								x={PAD.left - 4}
								y={y}
								textAnchor="end"
								dominantBaseline="middle"
								fontSize={9}
								fill="var(--bs-body-color)"
								opacity={0.7}
							>
								{v}
							</text>
						</g>
					);
				})}

				{/* X-axis labels */}
				{seasonLabels.map(({ season, i }) => (
					<text
						key={season}
						x={scaleX(i, n)}
						y={PAD.top + innerH + 14}
						textAnchor="middle"
						fontSize={9}
						fill="var(--bs-body-color)"
						opacity={0.7}
					>
						{season}
					</text>
				))}

				{/* POT line (dashed, secondary) */}
				<path
					d={toPath(potPoints)}
					fill="none"
					stroke="var(--bs-success)"
					strokeWidth={1.5}
					strokeDasharray="4 2"
					opacity={0.8}
				/>

				{/* OVR line (solid, primary) */}
				<path
					d={toPath(ovrPoints)}
					fill="none"
					stroke="var(--bs-primary)"
					strokeWidth={2}
				/>

				{/* End-point dots */}
				{n > 0 && (
					<>
						<circle
							cx={ovrPoints.at(-1)![0]}
							cy={ovrPoints.at(-1)![1]}
							r={3}
							fill="var(--bs-primary)"
						/>
						<circle
							cx={potPoints.at(-1)![0]}
							cy={potPoints.at(-1)![1]}
							r={3}
							fill="var(--bs-success)"
						/>
					</>
				)}
			</svg>

			{/* Legend */}
			<div className="d-flex gap-3 small">
				<span style={{ color: "var(--bs-primary)" }}>— {ovrLabel}</span>
				<span style={{ color: "var(--bs-success)" }}>-- {potLabel}</span>
			</div>
		</div>
	);
};

export default CareerOverviewChart;
